import {
  Download,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react';

import { usePlayer } from '@/contexts/PlayerContext';
import { Button } from '@/components/ui/Button';
import { Slider } from '@/components/ui/Slider';
import { Tooltip } from '@/components/ui/Tooltip';
import { ru } from '@/i18n/ru';
import { downloadUrl, formatClock } from '@/lib/format';

export function AudioPlayer() {
  const player = usePlayer();
  const { track } = player;

  return (
    <footer
      className="h-[var(--player-h)] shrink-0 border-t border-border bg-surface grid grid-cols-[minmax(140px,220px)_1fr_minmax(140px,200px)] items-center gap-3 px-3"
      data-testid="audio-player"
    >
      <div className="min-w-0">
        {track ? (
          <>
            <p className="truncate text-xs font-medium text-text">{track.filename}</p>
            <p className="truncate text-[11px] text-text-muted">
              {track.parentPath || ru.root}
            </p>
          </>
        ) : (
          <p className="text-xs text-text-muted">{ru.noTrack}</p>
        )}
      </div>

      <div className="flex min-w-0 flex-col items-stretch gap-1">
        <div className="flex items-center justify-center gap-1">
          <Tooltip content={ru.previous}>
            <Button
              size="icon"
              variant="ghost"
              aria-label={ru.previous}
              disabled={!track}
              onClick={player.prev}
            >
              <SkipBack className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
          <Tooltip content={player.isPlaying ? ru.pause : ru.play}>
            <Button
              size="icon"
              variant="outline"
              aria-label={player.isPlaying ? ru.pause : ru.play}
              disabled={!track}
              onClick={player.togglePlay}
              className="h-8 w-8"
            >
              {player.isPlaying ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
            </Button>
          </Tooltip>
          <Tooltip content={ru.next}>
            <Button
              size="icon"
              variant="ghost"
              aria-label={ru.next}
              disabled={!track}
              onClick={player.next}
            >
              <SkipForward className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
          <span className="ml-2 font-mono text-[10px] text-text-muted tabular-nums">
            {formatClock(player.currentTime)} / {formatClock(player.duration)}
          </span>
        </div>
        <div
          ref={player.waveformRef}
          className="h-9 w-full min-w-0 [&_wave]:cursor-pointer"
          aria-label={ru.waveform}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Tooltip content={player.muted ? ru.unmute : ru.mute}>
          <Button
            size="icon"
            variant="ghost"
            aria-label={player.muted ? ru.unmute : ru.mute}
            onClick={player.toggleMute}
          >
            {player.muted || player.volume === 0 ? (
              <VolumeX className="h-3.5 w-3.5" />
            ) : (
              <Volume2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </Tooltip>
        <Slider
          className="w-24"
          min={0}
          max={100}
          step={1}
          value={[Math.round((player.muted ? 0 : player.volume) * 100)]}
          onValueChange={(v) => player.setVolume((v[0] ?? 0) / 100)}
        />
        {track ? (
          <Tooltip content={ru.download}>
            <a
              href={downloadUrl(track.id)}
              download
              aria-label={`${ru.download} ${track.filename}`}
              className="inline-flex h-7 w-7 items-center justify-center text-text-muted hover:text-text hover:bg-row-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              <Download className="h-3.5 w-3.5" />
            </a>
          </Tooltip>
        ) : null}
      </div>
    </footer>
  );
}
